'use client';

import { useState } from 'react';
import { CreditCard, Wallet } from 'lucide-react';
import { useTRPC } from '@/lib/trpc/provider';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

/** Payment method the parent picks. Card→Stripe; wallets→NiHaoPay. */
export type PaymentMethodChoice = 'card' | 'alipay' | 'wechatpay' | 'unionpay';

type WalletChoice = 'alipay' | 'wechatpay' | 'unionpay';

const WALLETS: { value: WalletChoice; label: string }[] = [
  { value: 'alipay', label: 'Alipay' },
  { value: 'wechatpay', label: 'WeChat Pay' },
  { value: 'unionpay', label: 'UnionPay' },
];

/**
 * Small hook holding the selected method. Defaults to `card`, so a surface that
 * never renders the wallet options still submits the unchanged card flow.
 */
export function usePaymentMethod(initial: PaymentMethodChoice = 'card') {
  const [method, setMethod] = useState<PaymentMethodChoice>(initial);
  return { method, setMethod };
}

interface PaymentMethodSelectProps {
  value: PaymentMethodChoice;
  onChange: (method: PaymentMethodChoice) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Renders Card (Stripe) + Alipay / WeChat Pay / UnionPay (NiHaoPay). The wallet
 * options appear ONLY when the NiHaoPay rail is configured on the server — when
 * absent, only Card shows and the component is effectively a no-op
 * (`method` stays `card`).
 */
export function PaymentMethodSelect({
  value,
  onChange,
  disabled,
  className,
}: PaymentMethodSelectProps) {
  const trpc = useTRPC();
  const { data } = useQuery(trpc.enrollment.getAvailablePaymentMethods.queryOptions());

  // Until availability resolves, show card only (safe default).
  const nihaopay = data?.nihaopay ?? false;
  // Only the wallets enabled server-side (UnionPay off by default).
  const enabledWallets = data?.wallets ?? [];
  const wallets = WALLETS.filter((w) => enabledWallets.includes(w.value));

  const option = (
    selected: boolean,
    onClick: () => void,
    icon: React.ReactNode,
    label: string,
    key: string,
  ) => (
    <button
      key={key}
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors min-h-[44px]',
        selected
          ? 'border-[#fc800a] bg-[#fc800a]/5 text-[#1e306e]'
          : 'border-gray-200 bg-white text-[#374151] hover:border-gray-300',
        disabled && 'opacity-60 cursor-not-allowed',
      )}
    >
      {icon}
      {label}
    </button>
  );

  // Card-only → render nothing (keeps checkout surfaces clean when the wallet
  // rail is off or no wallets are enabled). The caller's default `method='card'`
  // already drives Stripe.
  if (!nihaopay || wallets.length === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-sm font-medium text-[#374151]">Payment method</p>
      <div className="grid grid-cols-2 gap-2">
        {option(
          value === 'card',
          () => onChange('card'),
          <CreditCard className="h-4 w-4" />,
          'Card',
          'card',
        )}
        {wallets.map((w) =>
          option(
            value === w.value,
            () => onChange(w.value),
            <Wallet className="h-4 w-4" />,
            w.label,
            w.value,
          ),
        )}
      </div>
    </div>
  );
}
